import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
    Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Tabs, Tab, Chip, IconButton, CircularProgress, Button, Typography, Box
} from '@mui/material';

// Icons
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import AddIcon from '@mui/icons-material/Add';
import ArticleIcon from '@mui/icons-material/Article';
import MapIcon from '@mui/icons-material/Map';

const API_URL = process.env.REACT_APP_API_URL;

const EditorIceriklerim = () => {
    const navigate = useNavigate();
    const [tabValue, setTabValue] = useState(0); // 0: Rotalar, 1: Bloglar
    const [rotalar, setRotalar] = useState([]);
    const [bloglar, setBloglar] = useState([]);
    const [loading, setLoading] = useState(true);
    const token = localStorage.getItem('token');

    // Verileri Çek
    const fetchData = async () => {
        setLoading(true);
        try {
            // Promise.all ile iki isteği paralel atıyoruz
            const [routesRes, blogsRes] = await Promise.all([
                axios.get(`${API_URL}/editor/my-routes`, { headers: { Authorization: `Bearer ${token}` } }),
                axios.get(`${API_URL}/editor/my-blogs`, { headers: { Authorization: `Bearer ${token}` } })
            ]);

            setRotalar(routesRes.data);
            setBloglar(blogsRes.data);

        } catch (error) {
            console.error("Veri çekme hatası:", error);
            if (error.response && error.response.status === 401) {
                navigate('/login');
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // İçerik Silme
    const handleDelete = async (id, type) => {
        if (!window.confirm("Bu içeriği kalıcı olarak silmek istediğinize emin misiniz?")) return;

        try {
            await axios.delete(`${API_URL}/editor/delete-content/${type}/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            // Listeyi güncelle
            if (type === 'route') {
                setRotalar(rotalar.filter(item => item.id !== id));
            } else {
                setBloglar(bloglar.filter(item => item.id !== id));
            }
        } catch (error) {
            alert("Silme işlemi sırasında hata oluştu.");
        }
    };

    // Durum Chip Rengi (Status: true -> Yayında, Status: false -> Onay Bekliyor)
    const getStatusChip = (status) => {
        if(status==1){
            return <Chip label="Onay Bekliyor" color="warning" size="small" variant="outlined" sx={{ color: '#fbbf24', borderColor: '#fbbf24' }} />;
        } else if (status==2) {
            return <Chip label="Yayında" color="success" size="small" variant="outlined" />;
        } else if(status==3){
            return <Chip label="Reddedildi" color="success" size="small" variant="outlined" sx={{ color: '#f20a0a', borderColor: '#f20a0a' }}/>;
        }
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Tablo Render Fonksiyonu
    const renderTable = (data, type) => (
        <TableContainer component={Paper} sx={{ bgcolor: '#1f2937', color: 'white' }} className="shadow-lg rounded-xl border border-gray-700">
            <Table sx={{ minWidth: 650 }}>
                <TableHead className="bg-gray-800">
                    <TableRow>
                        <TableCell sx={{ color: '#9ca3af', fontWeight: 'bold' }}>Görsel</TableCell>
                        <TableCell sx={{ color: '#9ca3af', fontWeight: 'bold' }}>Başlık</TableCell>
                        <TableCell sx={{ color: '#9ca3af', fontWeight: 'bold' }}>Oluşturulma Tarihi</TableCell>
                        <TableCell sx={{ color: '#9ca3af', fontWeight: 'bold' }}>Durum</TableCell>
                        <TableCell align="right" sx={{ color: '#9ca3af', fontWeight: 'bold' }}>İşlemler</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.length === 0 ? (
                        <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ color: 'gray', py: 5 }}>
                                Henüz içerik eklenmemiş.
                            </TableCell>
                        </TableRow>
                    ) : (
                        data.map((row) => (
                            <TableRow key={row.id} sx={{ '&:last-child td, &:last-child th': { border: 0 }, '&:hover': { bgcolor: '#374151' } }}>
                                <TableCell>
                                    <div className="w-16 h-12 bg-gray-700 rounded overflow-hidden">
                                        {row.image ? (
                                            <img src={row.image} alt={row.title} className="w-full h-full object-cover" />
                                        ) : (
                                            <div className="w-full h-full flex items-center justify-center text-xs text-gray-500">Resim Yok</div>
                                        )}
                                    </div>
                                </TableCell>
                                <TableCell component="th" scope="row" sx={{ color: 'white', fontWeight: '500' }}>
                                    {row.title}
                                </TableCell>
                                <TableCell sx={{ color: '#d1d5db' }}>
                                    {new Date(row.created_at).toLocaleDateString('tr-TR')}
                                </TableCell>
                                <TableCell>
                                    {getStatusChip(row.status)}
                                </TableCell>
                                <TableCell align="right">
                                    <IconButton size="small" sx={{ color: '#60a5fa' }} title="Önizle">
                                        <VisibilityIcon fontSize="small" />
                                    </IconButton>
                                    {/* Edit özelliği henüz yoksa disabled yapabiliriz veya sayfasına yönlendirebiliriz */}
                                    <IconButton size="small" sx={{ color: '#fbbf24' }} title="Düzenle">
                                        <EditIcon fontSize="small" />
                                    </IconButton>
                                    <IconButton 
                                        size="small" 
                                        sx={{ color: '#f87171' }} 
                                        onClick={() => handleDelete(row.id, type)}
                                        title="Sil"
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                </TableCell>
                            </TableRow>
                        ))
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    return (
        <div className="min-h-screen bg-gray-900 text-white p-4 md:p-8 font-sans">
            <div className="max-w-7xl mx-auto">
                {/* --- Header --- */}
                <div className="flex flex-col md:flex-row justify-between items-center mb-8 gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-indigo-500">
                            İçeriklerim
                        </h1>
                        <p className="text-gray-400 text-sm mt-1">Oluşturduğunuz tüm rota ve blog yazılarını buradan yönetebilirsiniz.</p>
                    </div>
                    <div className="flex gap-3">
                        <Button 
                            variant="contained" 
                            startIcon={<MapIcon />} 
                            onClick={() => navigate('/editor/yeni-rota')}
                            className="bg-gradient-to-r from-teal-600 to-teal-700 hover:from-teal-500 hover:to-teal-600"
                        >
                            Yeni Rota
                        </Button>
                        <Button 
                            variant="contained" 
                            startIcon={<ArticleIcon />} 
                            onClick={() => navigate('/editor/write-blog')}
                            className="bg-gradient-to-r from-indigo-600 to-indigo-700 hover:from-indigo-500 hover:to-indigo-600"
                        >
                            Yeni Blog
                        </Button>
                    </div>
                </div>

                {/* --- Tabs --- */}
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 4 }}>
                    <Tabs 
                        value={tabValue} 
                        onChange={handleTabChange} 
                        textColor="inherit"
                        indicatorColor="secondary"
                        sx={{
                            '& .MuiTab-root': { color: '#9ca3af' },
                            '& .Mui-selected': { color: '#2dd4bf' },
                            '& .MuiTabs-indicator': { backgroundColor: '#2dd4bf' }
                        }}
                    >
                        <Tab label={`Rotalarım (${rotalar.length})`} />
                        <Tab label={`Blog Yazılarım (${bloglar.length})`} />
                    </Tabs>
                </Box>

                {/* --- Content --- */}
                {loading ? (
                    <div className="flex justify-center items-center h-64">
                        <CircularProgress sx={{ color: '#2dd4bf' }} />
                    </div>
                ) : (
                    <>
                        {tabValue === 0 && (
                            <div className="animate-fade-in">
                                {renderTable(rotalar, 'route')}
                            </div>
                        )}
                        {tabValue === 1 && (
                            <div className="animate-fade-in">
                                {renderTable(bloglar, 'blog')}
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default EditorIceriklerim;